/* eslint-disable no-console */

/**
 * Eva Unit Core Initialization Utility
 *
 * This module handles the one-time initialization of the eva-unit-core WASM library.
 * The initialization loads the unit cache configuration that will be used for all
 * unit conversions throughout the application lifetime.
 */
import * as Sentry from "@sentry/react";

import axios from "axios";
import { DATA_SVC_RS_ADDRESS } from "constants/app.constants";

let isInitialized = false;
let initializationPromise: Promise<void> | null = null;
let evaUnitCoreModule: typeof import("eva-unit-core") | null = null;

/**
 * Initialize the eva-unit-core library with the unit cache configuration.
 * This function should be called once during application startup.
 * Subsequent calls will return the same promise and not re-initialize.
 *
 * @returns Promise that resolves when initialization is complete
 * @throws Error if initialization fails
 */
export async function initializeEvaUnitCore(): Promise<void> {
  // If already initialized, return immediately
  if (isInitialized) {
    return Promise.resolve();
  }

  // If initialization is in progress, return the existing promise
  if (initializationPromise) {
    return initializationPromise;
  }

  // Start initialization
  initializationPromise = (async () => {
    try {
      console.log("[EvaUnitCore] Starting initialization...");
      // Fetch unit configuration from data service API
      const apiUrl = `${DATA_SVC_RS_ADDRESS}/api/v1/data/unit-configuration`;
      const response = await axios.get(apiUrl);

      const unitCacheConfig = response.data;
      console.log("[EvaUnitCore] Successfully loaded unit configuration from API");

      // Dynamic import to avoid including WASM in initial chunk
      // The WASM fallback in index.html will handle MIME type errors
      const EvaUnitCore = await import("eva-unit-core");

      evaUnitCoreModule = EvaUnitCore;

      // Setup panic hook for better error messages
      EvaUnitCore.setup_panic_hook();

      // Convert the unit cache config to JSON string
      const configJson = JSON.stringify(unitCacheConfig);

      // Initialize the cache
      const result = EvaUnitCore.initialize_cache(configJson);

      console.log("[EvaUnitCore] Initialization result:", { result });
      console.log("[EvaUnitCore] Version:", { version: EvaUnitCore.version() });

      isInitialized = true;
    } catch (error) {
      Sentry.captureException(error);
      Sentry.logger.error("[EvaUnitCore] Initialization failed", { error });
      // Reset the promise so it can be retried
      initializationPromise = null;
      throw new Error(`Failed to initialize eva-unit-core: ${error}`);
    }
  })();

  return initializationPromise;
}

/**
 * Check if eva-unit-core has been initialized
 * @returns true if initialized, false otherwise
 */
export function isEvaUnitCoreInitialized(): boolean {
  return isInitialized;
}

/**
 * Reset the initialization state (primarily for testing purposes)
 */
export function resetEvaUnitCoreInitialization(): void {
  isInitialized = false;
  initializationPromise = null;
  evaUnitCoreModule = null;
}

/**
 * Get the eva-unit-core library instance.
 * Ensure initializeEvaUnitCore() has been called before using the library.
 *
 * @returns The eva-unit-core library or null if not initialized
 */
export function getEvaUnitCore(): typeof import("eva-unit-core") | null {
  if (!isInitialized || !evaUnitCoreModule) {
    Sentry.logger.warn(
      "[EvaUnitCore] Library accessed before initialization. " +
        "Call initializeEvaUnitCore() during app startup."
    );
    return null;
  }
  return evaUnitCoreModule;
}
